import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import ProduceTodayPriceDialog from "@/components/ProduceTodayPriceDialog";
import ProductDialog from "@/components/ProductDialog";
import ProductForm from "@/components/ProductForm";
import ImportWizard from "@/components/ImportWizard";
import BirlesikImport from "@/components/BirlesikImport";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BulkDeleteDialog from "@/components/BulkDeleteDialog";
import ProductSettings from "@/components/ProductSettings";
import BulkScanDialog from "@/components/BulkScanDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import Pagination from "@/components/Pagination";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Filter, AlertTriangle, Clock, Layers, Plus, FileSpreadsheet, Printer, Loader2, CircleDollarSign, Trash2, CheckSquare, X, Sparkles, Settings2, Ruler, ShieldCheck, TrendingUp, ScanLine, PackageCheck, PackageX } from "lucide-react";
import { isMarketFamilySector } from "@/lib/sectorVisibility";

const STAT_TONES = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  sky: "bg-sky-50 text-sky-700 ring-sky-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  zinc: "bg-zinc-100 text-zinc-600 ring-zinc-200",
};
function StatPill({ label, value, tone = "slate", active, onClick, testid }) {
  const clickable = !!onClick;
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={!clickable}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ring-1 ring-inset transition ${STAT_TONES[tone]} ${active ? "ring-2 ring-offset-1" : ""} ${clickable ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}>
      <span className="font-semibold tabular">{value ?? "—"}</span>
      <span className="opacity-80">{label}</span>
    </button>
  );
}

export default function Products() {
  const { can, isPrivileged, user, activeSector, activeSectorObj, feature } = useAuth();
  const secParam = activeSector && activeSector !== "full" ? activeSector : "";
  const chLabels = activeSectorObj?.channel_labels || { market: "Market", toptan: "Toptan" };
  const [channel, setChannel] = useState("market");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState(sp.get("filter") || "");
  const [catsMarket, setCatsMarket] = useState([]);
  const [catsToptan, setCatsToptan] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const listRequest = useRef(0);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showFullImport, setShowFullImport] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [mpOpen, setMpOpen] = useState(false);
  const [produceOpen, setProduceOpen] = useState(false);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [labelScope, setLabelScope] = useState("page");
  const [stats, setStats] = useState(null);
  const [aiImgOpen, setAiImgOpen] = useState(false);
  const [showBulkScan, setShowBulkScan] = useState(false);
  const [brandFilter, setBrandFilter] = useState("");
  const [brands, setBrands] = useState([]);
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [aiImgLimit, setAiImgLimit] = useState(50);
  const [aiImgStatus, setAiImgStatus] = useState(null);
  const [aiImgStarting, setAiImgStarting] = useState(false);
  const [aiAutoContinue, setAiAutoContinue] = useState(true);
  const [aiIntegrity, setAiIntegrity] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const aiRunningRef = useRef(false);
  const loadRef = useRef(null);
  const [activeCampaigns, setActiveCampaigns] = useState([]);

  const loadAiStatus = useCallback(async () => {
    try { const { data } = await api.get("/products/image-gen/status"); setAiImgStatus(data); return data; }
    catch { return null; }
  }, []);

  const startAiImages = async () => {
    setAiImgStarting(true);
    try {
      const { data } = await api.post("/products/generate-missing-images", { limit: Number(aiImgLimit) || 50, auto_continue: aiAutoContinue });
      if (data.started > 0) toast.success(data.message || `${data.started} ürün için AI görsel üretimi başladı`);
      else toast.info(data.message || "Görselsiz ürün kalmadı");
      aiRunningRef.current = true;
      loadAiStatus();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Başlatılamadı");
    } finally { setAiImgStarting(false); }
  };

  useEffect(() => {
    if (!aiImgOpen) return;
    loadAiStatus();
    const t = setInterval(async () => {
      const s = await loadAiStatus();
      if (s) {
        if (aiRunningRef.current && !s.running) {
          loadRef.current?.();
          if ((s.missing_total ?? 0) === 0) toast.success("🎉 Tüm ürün görselleri üretildi!");
          else if (!s.auto_continue) toast.info(`Görsel üretimi durdu · ${s.missing_total} ürün hâlâ görselsiz`);
        }
        aiRunningRef.current = s.running;
      }
    }, 2500);
    return () => clearInterval(t);
  }, [aiImgOpen, loadAiStatus]);
  useEffect(() => {
    if (!aiImgOpen) return;
    api.get("/products/ai-images/integrity").then(({ data }) => setAiIntegrity(data)).catch(() => setAiIntegrity(null));
  }, [aiImgOpen]);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/products/ai-images/reconcile");
      const n = (data.repaired_files || 0) + (data.relinked || 0) + (data.rehosted || 0);
      toast.success(n > 0
        ? `Onarıldı — ${data.repaired_files} eşleme kuruldu, ${data.relinked} yeniden bağlandı, ${data.rehosted} kalıcılaştırıldı`
        : "Tüm görseller zaten güvende");
      const { data: it } = await api.get("/products/ai-images/integrity"); setAiIntegrity(it);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Onarım başarısız"); }
    finally { setReconciling(false); }
  };

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/categories?channel=market${secParam ? `&sector=${secParam}` : ""}`).catch(() => ({ data: [] })),
      api.get(`/categories?channel=toptan${secParam ? `&sector=${secParam}` : ""}`).catch(() => ({ data: [] })),
    ]).then(([m, t]) => { setCatsMarket(m.data || []); setCatsToptan(t.data || []); });
  }, [secParam]);
  // Kategori listesi seçili kanala (Market/Toptan) göre daraltılır — market & toptan kategorileri karışmaz
  const cats = (channel === "toptan" || channel === "rota") ? catsToptan : channel === "all" ? [...catsMarket, ...catsToptan] : catsMarket;
  useEffect(() => { setCategory(""); }, [channel]);

  useEffect(() => { setPage(1); }, [channel, category, q, filter, locationId, pageSize]);

  useEffect(() => {
    if (isPrivileged) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [isPrivileged]);

  const load = useCallback(() => {
    const request = ++listRequest.current;
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams({ channel, page: String(page), page_size: String(pageSize) });
    if (category) params.set("category", category);
    if (brandFilter) params.set("brand", brandFilter);
    if (q) params.set("q", q);
    if (filter) params.set("filter", filter);
    if (locationId) params.set("location_id", locationId);
    if (secParam) params.set("sector", secParam);
    return api.get(`/products?${params}`).then((r) => {
      if (request === listRequest.current) setData(r.data);
    }).catch(() => {
      if (request === listRequest.current) setLoadError(true);
    }).finally(() => {
      if (request === listRequest.current) setLoading(false);
    });
  }, [channel, category, brandFilter, q, filter, locationId, page, pageSize, secParam]);
  loadRef.current = load;

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const loadStats = useCallback(() => {
    const params = new URLSearchParams();
    if (channel && channel !== "all") params.set("channel", channel);
    if (locationId) params.set("location_id", locationId);
    if (secParam) params.set("sector", secParam);
    const qs = params.toString();
    api.get(`/products/stats${qs ? `?${qs}` : ""}`).then((r) => setStats(r.data)).catch(() => {});
  }, [locationId, channel, secParam]);
  useEffect(() => { loadStats(); }, [loadStats]);
  const loadBrands = useCallback(() => { api.get("/products/brands").then((r) => setBrands(r.data.brands || [])).catch(() => {}); }, []);
  useEffect(() => { loadBrands(); }, [loadBrands]);
  const addBrand = async () => {
    return;
  };

  useEffect(() => { api.get("/campaigns").then((r) => setActiveCampaigns((r.data || []).filter((c) => c.active))).catch(() => setActiveCampaigns([])); }, []);
  const isCampaignProduct = (p) => activeCampaigns.some((c) => c.type !== "birthday" && (c.scope === "all" || (c.scope === "category" && (c.categories || []).includes(p.category)) || (c.scope === "products" && (c.product_ids || []).map(String).includes(String(p.id)))));

  const refreshOne = (updated) => {
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === updated.id ? updated : it)) }));
    setSelected(updated);
  };

  const handleEdit = (p) => { setSelected(null); setEditProduct(p); setShowForm(true); };
  const handleDeleted = (id) => {
    setSelected(null);
    setData((d) => ({ ...d, items: d.items.filter((it) => it.id !== id), total: Math.max(0, d.total - 1) }));
    loadStats();
  };
  const openNew = () => { setEditProduct(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditProduct(null); };

  const toggleSel = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const clearSel = () => setSelectedIds([]);
  const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };
  const selectAllMatching = async () => {
    const params = new URLSearchParams({ channel, page: "1", page_size: "100000" });
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    if (filter) params.set("filter", filter);
    if (locationId) params.set("location_id", locationId);
    if (secParam) params.set("sector", secParam);
    try { const all = await api.get(`/products?${params}`); setSelectedIds((all.data.items || []).map((p) => p.id)); }
    catch { toast.error("Ürünler alınamadı"); }
  };
  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/products/bulk-delete", { ids: selectedIds });
      toast.success(r.gated ? `${r.deleted} ürün silme onayına gönderildi` : `${r.deleted} ürün silindi${r.failed ? ` · ${r.failed} başarısız` : ""}`);
      setBulkOpen(false); exitSelect(); load(); loadStats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Toplu silme başarısız"); }
    finally { setBulkBusy(false); }
  };

  const doBulkOrderable = async (orderable) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/products/bulk-orderable", { ids: selectedIds, orderable });
      toast.success(`${r.updated} ürün ${orderable ? "siparişe AÇILDI" : "siparişe KAPATILDI"}`);
      exitSelect(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Toplu işlem başarısız"); }
    finally { setBulkBusy(false); }
  };

  const doCategoryOrderable = async (orderable) => {
    if (!category) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/products/bulk-orderable", { category, channel, orderable });
      toast.success(`"${category}" kategorisinde ${r.updated} ürün ${orderable ? "siparişe AÇILDI" : "siparişe KAPATILDI"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBulkBusy(false); }
  };

  const printShelfLabels = async () => {
    if (!data.total) return;
    setPrintingLabels(true);
    try {
      let items = data.items || [];
      if (labelScope === "all") {
        const params = new URLSearchParams({ channel, page: "1", page_size: "100000" });
        if (category) params.set("category", category);
        if (q) params.set("q", q);
        if (filter) params.set("filter", filter);
        if (locationId) params.set("location_id", locationId);
        const all = await api.get(`/products?${params}`);
        items = all.data.items || [];
      }
      if (items.length === 0) { setPrintingLabels(false); return; }
      const res = await api.post("/products/labels",
        { channel, items: items.map((p) => ({ product_id: p.id, copies: 1 })) },
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error("Etiketler oluşturulamadı"); }
    finally { setPrintingLabels(false); }
  };

  const FilterChip = ({ val, icon: Icon, label }) => (
    <button data-testid={`filter-${val || "all"}`} onClick={() => setFilter(val)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
        filter === val ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
      }`}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );

  if (loadError) return (
    <div role="alert" data-testid="products-load-error" className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
      <h1 className="font-heading text-2xl font-bold">Ürün Kartları</h1>
      <p>Ürünler yüklenemedi. Bu ekran boş katalog anlamına gelmez; bağlantıyı kontrol edip yeniden deneyin.</p>
      <Button onClick={load} variant="outline">Yeniden Dene</Button>
    </div>
  );

  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline">Katalog</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Ürün Kartları</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="products-purpose">Her ürün <b>tek karttan</b> yönetilir; kartta {(chLabels.market || "Market").toLowerCase()}, {(chLabels.toptan || "Toptan").toLowerCase()}, rota, e-ticaret ve pazaryeri fiyatları birlikte belirlenir. Aşağıdaki <b>{chLabels.market || "Market"}/{chLabels.toptan || "Toptan"}</b> yalnızca hangi fiyatın gösterileceğini seçer — ürünü ikinci kez girmenize gerek yok.</p>
        </div>
        <div className="inline-flex flex-wrap rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="channel-lens">
          {[["market", chLabels.market || "Market"], ["toptan", chLabels.toptan || "Toptan"], ["rota", "Rota"], ["eticaret", "E-Ticaret"], ["pazaryeri", "Pazaryeri"], ["all", "Tümü"]].map(([c, lbl]) => (
            <button key={c} data-testid={`channel-${c}`} onClick={() => setChannel(c)}
              className={`rounded-sm px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                channel === c ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"
              }`}>
              {lbl}
            </button>
          ))}
        </div>
        {can("catalog.write") && (
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="product-tools-btn" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
                  <Settings2 className="mr-1.5 h-4 w-4 text-brand-green" /> Ürün Ayarları
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Katalog Araçları</DropdownMenuLabel>
                {can("catalog.import") && (
                  <DropdownMenuItem data-testid="import-products-btn" onClick={() => setShowFullImport(true)}>
                    <FileSpreadsheet className="mr-2 h-4 w-4 text-brand-green" /> İçe Aktar (Excel)
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="export-products-btn" onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (brandFilter) params.set("brand", brandFilter);
                    if (channel === "market" || channel === "toptan") params.set("channel_type", channel);
                    if (secParam) params.set("sector", secParam);
                    const qs = params.toString();
                    const res = await api.get(`/products/export${qs ? `?${qs}` : ""}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const bLabel = brandFilter === "__none__" ? "markasiz" : brandFilter;
                    const a = document.createElement("a"); a.href = url; a.download = brandFilter ? `urunler-${bLabel}.xlsx` : "urunler.xlsx"; a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success(brandFilter ? `"${brandFilter === "__none__" ? "Markasız" : brandFilter}" markalı ürünler dışa aktarıldı` : "Ürünler dışa aktarıldı");
                  } catch { toast.error("Dışa aktarım başarısız"); }
                }}>
                  <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" /> {brandFilter ? `Excel'e Aktar (${brandFilter === "__none__" ? "Markasız" : brandFilter})` : "Excel'e Aktar"}
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem data-testid="ai-images-btn" onClick={() => setAiImgOpen(true)}>
                    <Sparkles className="mr-2 h-4 w-4 text-brand-orange" /> AI Görseller
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem data-testid="bulk-scan-btn" onClick={() => setShowBulkScan(true)}>
                    <ScanLine className="mr-2 h-4 w-4 text-sky-600" /> Toplu Barkod Tarama
                  </DropdownMenuItem>
                )}
                {can("price.write") && feature("cap.market_research", isMarketFamilySector(activeSector)) && (
                  <DropdownMenuItem data-testid="market-research-btn" onClick={() => setMpOpen(true)}>
                    <TrendingUp className="mr-2 h-4 w-4 text-brand-green" /> Piyasa Fiyatı Araştırması
                  </DropdownMenuItem>
                )}
                {can("price.write") && (
                  <DropdownMenuItem data-testid="produce-price-btn" onClick={() => setProduceOpen(true)}>
                    <TrendingUp className="mr-2 h-4 w-4 text-emerald-600" /> Manav — Bugünün Fiyatı
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="manage-units-btn" onClick={() => setUnitsOpen(true)}>
                  <Ruler className="mr-2 h-4 w-4 text-slate-500" /> Ölçü Birimleri
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button data-testid="products-select-toggle" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} variant="outline"
              className={selectMode ? "border-red-400 text-red-600 hover:bg-red-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}>
              {selectMode ? <><X className="mr-1.5 h-4 w-4" /> Seçimi Kapat</> : <><CheckSquare className="mr-1.5 h-4 w-4" /> Seç / Sil</>}
            </Button>
            <Button data-testid="new-product-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Ürün
            </Button>
          </div>
        )}
      </div>

      {stats && (
        <div data-testid="products-stats" className="flex flex-wrap gap-2">
          <StatPill label="Toplam" value={stats.total} tone="zinc" active={filter === ""} onClick={() => setFilter("")} testid="stat-total" />
          <StatPill label="Aktif" value={stats.active} tone="slate" testid="stat-active" />
          <StatPill label="Satışa Hazır" value={stats.ready_to_sell} tone="emerald" testid="stat-ready" />
          <StatPill label="Stokta" value={stats.in_stock} tone="sky" testid="stat-instock" />
          <StatPill label="Stok Yok" value={stats.out_of_stock} tone="rose" testid="stat-outstock" />
          <StatPill label="Fiyatsız" value={stats.unpriced} tone="amber" active={filter === "unpriced"} onClick={() => setFilter(filter === "unpriced" ? "" : "unpriced")} testid="stat-unpriced" />
          <StatPill label="Arşiv" value={stats.archived} tone="zinc" active={filter === "archived"} onClick={() => setFilter(filter === "archived" ? "" : "archived")} testid="stat-archived" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ürün, SKU veya barkod ara..." className="pl-9" />
        </div>
        <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
          <option value="">Tüm Kategoriler</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        {category && can("catalog.write") && (
          <div className="flex items-center gap-1.5" data-testid="category-orderable-tools">
            <Button size="sm" variant="outline" data-testid="category-orderable-on" disabled={bulkBusy} onClick={() => doCategoryOrderable(true)} className="h-10 border-emerald-400 text-emerald-700 hover:bg-emerald-50" title={`"${category}" kategorisindeki TÜM ürünleri mağaza taleplerine aç`}>
              <PackageCheck className="mr-1 h-4 w-4" /> Kategoriyi Aç
            </Button>
            <Button size="sm" variant="outline" data-testid="category-orderable-off" disabled={bulkBusy} onClick={() => doCategoryOrderable(false)} className="h-10 border-amber-400 text-amber-700 hover:bg-amber-50" title={`"${category}" kategorisindeki TÜM ürünleri mağaza taleplerine kapat`}>
              <PackageX className="mr-1 h-4 w-4" /> Kategoriyi Kapat
            </Button>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <select data-testid="brand-filter" value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPage(1); }}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="">Tüm Markalar</option>
            <option value="__none__">Markasız</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        {isPrivileged && (
          <select data-testid="product-location" value={locationId} onChange={(e) => setLocationId(e.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="">Tüm Lokasyonlar (toplam)</option>
            {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip val="" icon={Layers} label="Tümü" />
        <FilterChip val="unpriced" icon={CircleDollarSign} label="Fiyatsız" />
        <FilterChip val="low_stock" icon={AlertTriangle} label="Düşük Stok" />
        <FilterChip val="expiring" icon={Clock} label="SKT Yaklaşan" />
        <div className="ml-auto flex items-center gap-1.5">
          <select data-testid="label-scope" value={labelScope} onChange={(e) => setLabelScope(e.target.value)}
            className="h-8 rounded-md border border-input bg-white px-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="page">{`Bu sayfadakiler (${data.items.length})`}</option>
            <option value="all">{`Tümü (${data.total})`}</option>
          </select>
          <Button data-testid="shelf-labels-btn" onClick={printShelfLabels} disabled={printingLabels || !data.total}
            variant="outline" size="sm" className="h-8 border-brand-green text-brand-green hover:bg-brand-green/5">
            {printingLabels ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1 h-3.5 w-3.5" />} Raf Etiketleri Yazdır
          </Button>
        </div>
        <span className="self-center text-xs text-slate-400 tabular">{data.total} ürün</span>
      </div>

      {selectMode && (
        <div data-testid="products-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-md bg-red-50 px-3 py-2 ring-1 ring-inset ring-red-200">
          <span className="text-sm font-semibold text-red-700" data-testid="products-selected-count">{selectedIds.length} seçili</span>
          <Button size="sm" variant="outline" data-testid="products-select-all" onClick={selectAllMatching} className="h-8 border-slate-300">Eşleşen tümünü seç ({data.total})</Button>
          <Button size="sm" variant="outline" data-testid="products-clear-sel" onClick={clearSel} className="h-8 border-slate-300">Temizle</Button>
          <Button size="sm" variant="outline" data-testid="products-bulk-orderable-on" disabled={selectedIds.length === 0 || bulkBusy} onClick={() => doBulkOrderable(true)} className="h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50">
            <PackageCheck className="mr-1.5 h-4 w-4" /> Siparişe Aç
          </Button>
          <Button size="sm" variant="outline" data-testid="products-bulk-orderable-off" disabled={selectedIds.length === 0 || bulkBusy} onClick={() => doBulkOrderable(false)} className="h-8 border-amber-400 text-amber-700 hover:bg-amber-50">
            <PackageX className="mr-1.5 h-4 w-4" /> Siparişe Kapat
          </Button>
          <Button size="sm" data-testid="products-bulk-delete" disabled={selectedIds.length === 0} onClick={() => setBulkOpen(true)} className="ml-auto h-8 bg-red-600 text-white hover:bg-red-700">
            <Trash2 className="mr-1.5 h-4 w-4" /> Seçili {selectedIds.length} Ürünü Sil
          </Button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 animate-pulse rounded-md bg-slate-200/60" />)}
        </div>
      ) : data.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-300 py-16">
          <Filter className="mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">Kriterlere uygun ürün bulunamadı.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {data.items.map((p) => (
            <ProductCard key={p.id} product={p} lens={channel} onClick={() => setSelected(p)}
              selectable={selectMode} selected={selectedIds.includes(p.id)} onToggleSelect={() => toggleSel(p.id)} campaignActive={isCampaignProduct(p)} />
          ))}
        </div>
      )}

      {!loading && data.items.length > 0 && (
        <Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} onPageSize={setPageSize} testid="products-pagination" />
      )}

      {selected && (
        <ProductDialog product={selected} onClose={() => setSelected(null)} onUpdate={refreshOne}
          canPrice={can("price.write")} onEdit={handleEdit} onDeleted={handleDeleted} />
      )}

      <ProductForm open={showForm} onClose={closeForm} onSaved={load} editProduct={editProduct} />
      <BulkScanDialog open={showBulkScan} onClose={() => setShowBulkScan(false)} onCreated={load} />
      <Dialog open={showFullImport} onOpenChange={(o) => { setShowFullImport(o); if (!o) load(); }}>
        <DialogContent data-testid="product-import-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><FileSpreadsheet className="h-5 w-5 text-brand-green" /> Ürün İçe Aktarma</DialogTitle>
            <DialogDescription>Excel'den toplu ürün/kategori içe aktarma — yalnızca yetkili kullanıcılar erişebilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <ImportWizard />
            {user?.role === "admin" && <BirlesikImport />}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={unitsOpen} onOpenChange={setUnitsOpen}>
        <DialogContent data-testid="units-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Ruler className="h-5 w-5 text-brand-green" /> Ölçü Birimleri</DialogTitle>
            <DialogDescription>Belge ve ürün kalemlerinde kullanılan ölçü birimlerini yönetin. Değişiklikler anında kaydedilir.</DialogDescription>
          </DialogHeader>
          <ProductSettings hideHeader view="units" />
        </DialogContent>
      </Dialog>
      <Dialog open={mpOpen} onOpenChange={setMpOpen}>
        <DialogContent data-testid="market-research-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><TrendingUp className="h-5 w-5 text-brand-green" /> Piyasa Fiyatı Araştırması</DialogTitle>
            <DialogDescription>Ürünlerin güncel piyasa fiyatını araştırıp piyasa fiyatı alanına yazar. "Otomatik bul" ile fiyatsız ürün kalmayana kadar devam eder.</DialogDescription>
          </DialogHeader>
          <ProductSettings hideHeader view="market" />
        </DialogContent>
      </Dialog>
      <ProduceTodayPriceDialog open={produceOpen} onOpenChange={setProduceOpen} />
      <BulkDeleteDialog open={bulkOpen} count={selectedIds.length} label="ürün" busy={bulkBusy} onClose={() => setBulkOpen(false)} onConfirm={doBulkDelete} />

      <Dialog open={aiImgOpen} onOpenChange={setAiImgOpen}>
        <DialogContent data-testid="ai-images-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-brand-orange" /> AI Ürün Görselleri</DialogTitle>
            <DialogDescription>Görselsiz ürünler için Gemini (Nano Banana) ile otomatik ürün fotoğrafı üretir. Her görsel LLM kredisi harcar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-sm ring-1 ring-inset ring-slate-200">
              <div className="flex items-center justify-between"><span className="text-slate-500">Görselsiz ürün</span><span className="font-bold tabular text-slate-900" data-testid="ai-missing-count">{aiImgStatus?.missing_total ?? "—"}</span></div>
            </div>
            {aiImgStatus?.running ? (
              <div className="space-y-2" data-testid="ai-progress">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">Üretiliyor…{aiImgStatus.auto_continue && <span className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-semibold text-brand-orange">Otomatik devam</span>}</span>
                  <span className="tabular">{aiImgStatus.done}/{aiImgStatus.total}{aiImgStatus.failed ? ` · ${aiImgStatus.failed} hata` : ""}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-orange transition-all" style={{ width: `${aiImgStatus.total ? Math.round((aiImgStatus.done / aiImgStatus.total) * 100) : 0}%` }} /></div>
                <div className="flex items-center justify-between text-[11px] text-slate-400">
                  <span>Kalan görselsiz: <b className="tabular text-slate-600">{aiImgStatus.missing_total ?? "—"}</b></span>
                  {aiImgStatus.daily_cap ? <span>Bugün: <b className="tabular text-slate-600">{aiImgStatus.daily_used}/{aiImgStatus.daily_cap}</b></span> : null}
                </div>
                <p className="text-[11px] text-slate-400">Bu pencereyi kapatabilirsiniz; arka planda sürer.</p>
                <Button data-testid="ai-cancel-btn" variant="outline" onClick={async () => { try { await api.post("/products/image-gen/cancel"); toast.info("Görsel üretimi durduruluyor…"); loadAiStatus(); } catch { toast.error("İptal edilemedi"); } }}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50">
                  <X className="mr-2 h-4 w-4" /> Üretimi Durdur
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Bu turda kaç ürün? (kredi kontrolü için)</label>
                  <select data-testid="ai-limit-select" value={aiImgLimit} onChange={(e) => setAiImgLimit(Number(e.target.value))}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange">
                    {[10, 25, 50, 100, 250, 500].map((n) => <option key={n} value={n}>{`${n} ürün`}</option>)}
                  </select>
                </div>
                <label data-testid="ai-auto-continue" className="flex cursor-pointer items-start gap-2 rounded-md bg-amber-50/60 p-2.5 ring-1 ring-inset ring-amber-100">
                  <input type="checkbox" checked={aiAutoContinue} onChange={(e) => setAiAutoContinue(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-orange focus:ring-brand-orange" />
                  <span className="text-[12px] text-slate-600"><b className="text-slate-800">Bitince otomatik devam et</b> — tüm görselsiz ürünler tamamlanana kadar sıradaki grupları kendiliğinden üretir (kredi harcar).</span>
                </label>
                {aiImgStatus?.finished_at && aiImgStatus?.total > 0 && (
                  <p className="text-[11px] text-emerald-600" data-testid="ai-last-result">Son iş: {aiImgStatus.done}/{aiImgStatus.total} üretildi{aiImgStatus.failed ? `, ${aiImgStatus.failed} hata` : ""}.</p>
                )}
                <Button data-testid="ai-start-btn" onClick={startAiImages} disabled={aiImgStarting || (aiImgStatus?.missing_total ?? 0) === 0}
                  className="w-full bg-brand-orange text-white hover:bg-orange-600">
                  {aiImgStarting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Görselleri Üret
                </Button>
              </>
            )}
            {aiIntegrity && (
              <div data-testid="ai-integrity" className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-brand-green" /> Görsel Kalıcılığı</span>
                  {aiIntegrity.healthy
                    ? <span data-testid="ai-integrity-ok" className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">Tümü güvende</span>
                    : <span data-testid="ai-integrity-warn" className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">Onarım gerekli</span>}
                </div>
                <p className="text-[11px] text-slate-500">
                  {aiIntegrity.ai_image_count} AI görseli kalıcı depoda saklanıyor ve sisteme eşli.{" "}
                  {(aiIntegrity.orphan_files > 0 || aiIntegrity.relinkable_products > 0 || aiIntegrity.external_url_count > 0)
                    ? `${aiIntegrity.orphan_files} kopuk eşleme · ${aiIntegrity.relinkable_products} yeniden bağlanabilir · ${aiIntegrity.external_url_count} harici link.`
                    : "Kırık veya harici (kırılabilir) bağlantı yok."}
                </p>
                {!aiIntegrity.healthy && (
                  <Button data-testid="ai-reconcile-btn" onClick={runReconcile} disabled={reconciling} size="sm" variant="outline" className="mt-2 border-brand-green/40 text-brand-green hover:bg-emerald-50">
                    {reconciling ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />} Bütünlüğü Onar
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
